import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  FileText,
  Mail,
  RefreshCw,
  Ban,
  CheckCircle2,
  Hourglass,
  Eye,
  BellRing,
  BellOff,
  Settings2,
  CircleDollarSign,
  Info,
  Plus,
  Wallet,
} from 'lucide-react';
import useCobroStore from '../../../store/cobro';
import { showToast } from '../../../utils/showToast';
import { apiErrorMessageAsync } from '../../../utils/apiErrorMessage';
import { safeFileUrl } from '../../../utils/safeUrl';
import { Button } from '../../../components/ui/Button';
import { CopyButton } from '../../../components/ui/CopyButton';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Modal } from '../../../components/ui/Modal';
import { Skeleton } from '../../../components/ui/Skeleton';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { PendingBackendNotice } from '../../../components/ui/PendingBackendNotice';
import { useIsMobile } from '../../../hooks/useIsMobile';
import PaymentStatusBadge from './components/PaymentStatusBadge';
import { NoteDueDateLine } from './components/DueDateInfo';
import SendReminderModal from './components/SendReminderModal';
import ReminderHistoryCard from './components/ReminderHistoryCard';
import CommerceCollectionRuleDrawer from './components/CommerceCollectionRuleDrawer';
import useCollectionsSetup from './hooks/useCollectionsSetup';
import useNoteReminders from './hooks/useNoteReminders';
import { REMINDER_CHANNELS, isCollectibleNote } from './utils/collectionReminders';
import {
  formatCurrency,
  formatDate,
  CHARGE_MODE_LABELS,
  displayPaymentStatus,
  resolveCompanyName,
} from './utils/cobroHelpers';
import { sortLineItems, nextSortState } from './utils/lineSort';
import { retypeProofBlob, PROOF_UNKNOWN_TYPE } from './utils/proofBlob';
import { isHoldingNote, noteHoldingId } from './utils/holdings';
import {
  PAYMENT_METHODS,
  amountDueOf,
  hasValidPrecision,
  paidAmountOf,
  paidReferencesOf,
  paymentErrorMessage,
  paymentMethodLabelKey,
  isPaymentVoidable,
  paidAtIso,
  parseAmountInput,
  registeredByLabelKey,
  roundingTolerance,
  todayYMD,
  tomorrowYMD,
} from './utils/payments';
import HoldingNoteSummary from './components/holdings/HoldingNoteSummary';

// `accent` = good news (green); `warning` = money still owed (amber).
const SummaryCard = ({ label, value, accent, warning, negative }) => {
  const tone = accent ? '--accent' : warning ? '--warning' : '';
  return (
    <div className={`bill-summary__card${tone ? ` bill-summary__card${tone}` : ''}`}>
      <div className="bill-summary__label">{label}</div>
      <div
        className={`bill-summary__value${tone ? ` bill-summary__value${tone}` : ''}`}
        style={negative ? { color: 'rgb(var(--status-error))' } : undefined}
      >
        {value}
      </div>
    </div>
  );
};

// Folio columns: # · Descripción · Fecha · Tarifa · Valor
// La primera pista es flexible con piso, NO una longitud fija: contiene el número de
// seguimiento del paquete (7-10 dígitos monoespaciados), no un ordinal. Con `48px` el
// texto se salía de la celda y se pintaba encima de "Descripción". El resto va en
// minmax(0, Nfr) para que ningún contenido largo pueda empujar el ancho total: el
// contenedor es .bill-card--clip (overflow:hidden) y el excedente se recortaría en
// silencio por la derecha, empezando por "Valor".
const LINE_COLS =
  'minmax(84px, 0.9fr) minmax(0, 1.8fr) minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 1fr)';
// Historial de pagos: Fecha · Monto · Método · Referencia · Quién · Comentario · Acciones
const PAYMENT_COLS =
  'minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 0.9fr) minmax(0, 1.4fr) auto';
const LINE_PAGE = 50; // Se muestran las líneas por bloques para no montar cientos de nodos.

// Cabecera ordenable. Mantiene un único hijo directo por columna: .bill-grid__head es un
// CSS grid posicional y meter un elemento de más correría todas las columnas.
const SortHeader = ({ colKey, label, align, sort, onSort, title }) => {
  const active = sort.key === colKey;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <span
      className={`bill-grid__hcell${align === 'end' ? ' bill-grid__hcell--end' : ''}`}
      role="columnheader"
      aria-sort={!active ? 'none' : sort.dir === 'asc' ? 'ascending' : 'descending'}
    >
      <button type="button" className="bill-grid__sort" onClick={() => onSort(colKey)} title={title}>
        {label}
        <Icon
          className={`w-3 h-3 bill-grid__sort__icon${active ? '' : ' bill-grid__sort__icon--idle'}`}
          aria-hidden
        />
      </button>
    </span>
  );
};

// Por qué no se puede mandar un recordatorio de la nota ahora (clave i18n), o null. Billing
// valida lo mismo (409/503); acá se adelanta para explicar el botón deshabilitado.
const remindBlockReason = (note, delivery, cooldown) => {
  if (!isCollectibleNote(note)) {
    return 'La nota no admite recordatorios: está pagada, en revisión, anulada o sin monto a cobrar.';
  }
  if (!delivery) return null;
  if (!delivery.schedulerEnabled) return 'El envío de recordatorios no está habilitado en este entorno.';
  const configured = REMINDER_CHANNELS.filter((c) =>
    c === 'email' ? delivery.emailConfigured : delivery.whatsappConfigured,
  );
  if (configured.length === 0) return 'Ningún canal de envío está configurado en este entorno.';
  if (configured.every((c) => cooldown[c])) {
    return 'Ya se envió un recordatorio a este comercio por todos sus canales en las últimas 24 h.';
  }
  return null;
};

const CobroNoteDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const {
    selectedNote,
    loadingDetail,
    fetchNote,
    clearSelectedNote,
    voidNote,
    voiding,
    notifyNote,
    notifying,
    regeneratePdf,
    regenerating,
    confirmPayment,
    confirming,
    rejectPayment,
    rejecting,
    fetchTransferProof,
    registerPayment,
    registeringPayment,
    voidPayment,
    voidingPayment,
    listCredits,
  } = useCobroStore((s) => ({
    selectedNote: s.selectedNote,
    loadingDetail: s.loadingDetail,
    fetchNote: s.fetchNote,
    clearSelectedNote: s.clearSelectedNote,
    voidNote: s.voidNote,
    voiding: s.voiding,
    notifyNote: s.notifyNote,
    notifying: s.notifying,
    regeneratePdf: s.regeneratePdf,
    regenerating: s.regenerating,
    confirmPayment: s.confirmPayment,
    confirming: s.confirming,
    rejectPayment: s.rejectPayment,
    rejecting: s.rejecting,
    fetchTransferProof: s.fetchTransferProof,
    registerPayment: s.registerPayment,
    registeringPayment: s.registeringPayment,
    voidPayment: s.voidPayment,
    voidingPayment: s.voidingPayment,
    listCredits: s.listCredits,
  }));

  const [lineFilter, setLineFilter] = useState('all');
  // key null = orden original de la nota (el mismo del PDF). Es un tercer estado del ciclo,
  // no un accidente: el operador tiene que poder volver al orden del documento emitido.
  const [sort, setSort] = useState({ key: null, dir: null });
  const [visibleLines, setVisibleLines] = useState(LINE_PAGE);
  const [voidModal, setVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [loadingProof, setLoadingProof] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  const [ruleOpen, setRuleOpen] = useState(false);
  // Registro de pago (parcial) y anulación de un pago.
  const [paymentModal, setPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayYMD);
  const [paymentMethod, setPaymentMethod] = useState('transfer');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentComment, setPaymentComment] = useState('');
  const [paymentFormError, setPaymentFormError] = useState('');
  const [voidPaymentTarget, setVoidPaymentTarget] = useState(null);
  const [voidPaymentReason, setVoidPaymentReason] = useState('');
  const [voidPaymentError, setVoidPaymentError] = useState('');
  // Monto real recibido al confirmar una transferencia/efectivo en revisión.
  const [receivedAmount, setReceivedAmount] = useState('');
  // Saldos a favor abiertos del deudor (best-effort: si falla, no se muestra nada).
  const [creditInfo, setCreditInfo] = useState(null);

  // Cobranza (recordatorios, regla del comercio). Solo staff: un rol sin permiso recibe 403
  // de billing y no ve nada de esto.
  const collections = useCollectionsSetup();
  const canManageCollections = collections.error?.status !== 403;
  // Nota consolidada de un holding: sin recordatorios (fase posterior; billing responde 409
  // holding_reminders_not_supported), así que ni se pide su historial.
  const holdingNote = !!selectedNote && isHoldingNote(selectedNote);
  const reminders = useNoteReminders(selectedNote, {
    enabled: !!selectedNote && canManageCollections && !holdingNote,
  });

  const allItems = useMemo(() => selectedNote?.lineItems ?? [], [selectedNote]);
  const oversizedCount = useMemo(() => allItems.filter((li) => li.sizeCategory).length, [allItems]);
  const zoneCount = useMemo(() => allItems.filter((li) => li.zonePriced).length, [allItems]);
  const manualCount = useMemo(() => allItems.filter((li) => li.manualPriced).length, [allItems]);
  // Paquetes devueltos: su importe está en la línea pero se compensó con el ajuste
  // "Pedidos devueltos". Este filtro es la única forma de saber CUÁLES entraron.
  const returnedCount = useMemo(() => allItems.filter((li) => li.returned).length, [allItems]);

  const shownItems = useMemo(
    () =>
      allItems.filter((li) =>
        lineFilter === 'oversized'
          ? !!li.sizeCategory
          : lineFilter === 'zone'
            ? !!li.zonePriced
            : lineFilter === 'manual'
              ? !!li.manualPriced
              : lineFilter === 'returned'
                ? !!li.returned
                : true,
      ),
    [allItems, lineFilter],
  );
  // El orden se aplica DESPUÉS del filtro y ANTES del corte de `visibleLines`: de lo
  // contrario se ordenarían solo las primeras 50 líneas y el resultado sería falso.
  // Los lineItems llegan completos del backend (GET /billing/cobro/:id no pagina), así
  // que esto ordena la nota entera, no una página.
  const sortedItems = useMemo(() => sortLineItems(shownItems, sort), [shownItems, sort]);

  const pagedItems = sortedItems.slice(0, visibleLines);

  const toggleSort = (key) => setSort((s) => nextSortState(s, key));

  // Al cambiar de filtro o de orden se reinicia el bloque visible.
  useEffect(() => {
    setVisibleLines(LINE_PAGE);
  }, [lineFilter, sort]);

  const adjustmentsTotal = useMemo(
    () =>
      (selectedNote?.adjustments ?? []).reduce(
        (s, a) => s + (a.type === 'discount' ? -(a.amount ?? 0) : a.amount ?? 0),
        0,
      ),
    [selectedNote],
  );

  useEffect(() => {
    fetchNote(id).catch(() => {
      showToast(t('No se pudo cargar la nota de cobro.'), 'error');
      navigate('/admin/cobro');
    });
    return () => clearSelectedNote();
  }, [id, fetchNote, clearSelectedNote, navigate, t]);

  const handleVoid = async () => {
    if (!voidReason.trim()) {
      showToast(t('Debes indicar un motivo para anular la nota.'), 'warning');
      return;
    }
    try {
      await voidNote(id, voidReason.trim());
      showToast(t('Nota anulada. Los paquetes quedaron disponibles para otra nota.'), 'success');
      setVoidModal(false);
      setVoidReason('');
    } catch (err) {
      showToast(err?.response?.data?.message ?? t('No se pudo anular la nota.'), 'error');
    }
  };

  const handleRegeneratePdf = async () => {
    try {
      const res = await regeneratePdf(id);
      if (res?.ok) {
        showToast(t('PDF regenerado.'), 'success');
      } else {
        showToast(
          res?.reason === 'pdf_service_unavailable'
            ? t('El servicio de PDF no está disponible en el servidor.')
            : t('No se pudo regenerar el PDF.'),
          'warning',
        );
      }
    } catch (err) {
      showToast(err?.response?.data?.message ?? t('No se pudo regenerar el PDF.'), 'error');
    }
  };

  const handleNotify = async () => {
    try {
      const res = await notifyNote(id);
      if (res?.sent) {
        showToast(t('Correo enviado a {{n}} contacto(s).', { n: res.recipients }), 'success');
        return;
      }
      const reasons = {
        // La nota de un holding va a los correos de su perfil de cobro, no a los contactos de
        // facturación de un comercio.
        no_billing_contacts: holdingNote
          ? t('El holding no tiene correos de facturación en su perfil de cobro.')
          : t('El comercio no tiene contactos de facturación cargados.'),
        config_missing: t('Falta configurar el correo en el servidor (SendGrid/Users).'),
        notification_service_unavailable: t('El servicio de notificaciones no está disponible.'),
      };
      showToast(
        reasons[res?.reason] ??
          t('No se pudo enviar el correo ({{reason}}).', { reason: res?.reason ?? 'desconocido' }),
        'warning',
      );
    } catch (err) {
      showToast(err?.response?.data?.message ?? t('No se pudo enviar el correo.'), 'error');
    }
  };

  // Abre el comprobante en una pestaña nueva (descarga autenticada como blob: la URL
  // pública del archivo nunca se expone en la UI). El tipo se deduce de los BYTES, nunca
  // del Content-Type: el archivo lo sube el comercio y un `text/html` se ejecutaría en el
  // origen de la app (la blob: hereda el origen) con el token del operador a mano.
  const handleViewProof = async () => {
    setLoadingProof(true);
    try {
      const proof = await retypeProofBlob(await fetchTransferProof(id));
      if (proof.type === PROOF_UNKNOWN_TYPE) {
        showToast(t('El comprobante no es un PDF ni una imagen: se descargará sin abrirlo.'), 'warning');
      }
      const url = URL.createObjectURL(proof);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      // getTransferProof pide la respuesta como blob: el cuerpo del error también llega como
      // Blob, así que `.message` siempre sería undefined. El helper async lo lee y parsea.
      showToast(await apiErrorMessageAsync(err, t('No se pudo cargar el comprobante.')), 'error');
    } finally {
      setLoadingProof(false);
    }
  };

  const handleConfirm = async () => {
    const amount = parseAmountInput(receivedAmount);
    const currency = selectedNote?.currency ?? 'CLP';
    if (!Number.isFinite(amount) || amount <= 0) {
      showToast(t('El monto recibido debe ser mayor que cero.'), 'warning');
      return;
    }
    if (!hasValidPrecision(amount, currency)) {
      showToast(
        currency === 'CLP'
          ? t('El monto debe ser un número entero para {{currency}}.', { currency })
          : t('El monto debe tener hasta 2 decimales.'),
        'warning',
      );
      return;
    }
    try {
      await confirmPayment(id, amount);
      // Con un monto menor al saldo la nota queda parcial: el refresco del detalle
      // muestra el estado real, así que el aviso no afirma que quedó pagada.
      showToast(t('Pago confirmado.'), 'success');
    } catch (err) {
      showToast(err?.response?.data?.message ?? t('No se pudo confirmar el pago.'), 'error');
    }
  };

  const handleReject = async () => {
    try {
      await rejectPayment(id, rejectReason.trim() || undefined);
      showToast(t('Pago rechazado. La nota volvió a impaga.'), 'success');
      setRejectModal(false);
      setRejectReason('');
    } catch (err) {
      showToast(err?.response?.data?.message ?? t('No se pudo rechazar el pago.'), 'error');
    }
  };

  // Saldo a favor del deudor (comercio u holding) para mostrarlo en la tarjeta Pagos.
  useEffect(() => {
    if (!selectedNote) {
      setCreditInfo(null);
      return undefined;
    }
    const holdingId = noteHoldingId(selectedNote);
    const companyId = String(
      (typeof selectedNote.companyId === 'object'
        ? selectedNote.companyId?._id
        : selectedNote.companyId) ?? '',
    );
    if (!holdingId && !companyId) {
      setCreditInfo(null);
      return undefined;
    }
    let active = true;
    const params = holdingId
      ? { holdingId, onlyOpen: true }
      : { companyId, onlyOpen: true };
    Promise.resolve(listCredits(params))
      .then((data) => {
        if (active) setCreditInfo(data ?? null);
      })
      .catch(() => {
        if (active) setCreditInfo(null);
      });
    return () => {
      active = false;
    };
  }, [selectedNote, listCredits]);

  // Al abrir el detalle de una nota en revisión, el monto recibido arranca en su saldo.
  useEffect(() => {
    if (!selectedNote || selectedNote.paymentStatus !== 'in_review') return;
    setReceivedAmount(String(amountDueOf(selectedNote)));
  }, [selectedNote]);

  if (loadingDetail || !selectedNote) {
    return (
      <div className="bill-page" role="status" aria-live="polite">
        <span className="sr-only">{t('Cargando…')}</span>
        <Skeleton className="h-4 w-32" style={{ marginBottom: '1rem' }} />
        <div className="bill-card bill-card--pad">
          <div className="d-flex justify-content-between" style={{ gap: '1rem', flexWrap: 'wrap' }}>
            <div className="d-grid" style={{ gap: '0.5rem' }}>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <div className="d-flex" style={{ gap: '0.5rem' }}>
              <Skeleton className="h-8 w-24 rounded-md" />
              <Skeleton className="h-8 w-24 rounded-md" />
            </div>
          </div>
        </div>
        <div className="bill-summary">
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="bill-summary__card" key={i}>
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-6 w-24" style={{ marginTop: '0.5rem' }} />
            </div>
          ))}
        </div>
        <div className="bill-card bill-card--clip" style={{ marginTop: '1rem' }}>
          <TableSkeleton rows={8} columns={5} className="border-0" />
        </div>
      </div>
    );
  }

  const n = selectedNote;
  const cur = n.currency ?? 'CLP';
  // Desglose de IVA según el tratamiento del contrato (taxMode) persistido en la nota.
  const hasTax = n.taxAmount > 0;
  const isInclusive = n.taxMode === 'inclusive';
  const taxRatePct = Math.round((n.taxRate || 0) * 100);
  const taxLabel = taxRatePct > 0 ? t('IVA ({{pct}}%)', { pct: taxRatePct }) : t('Impuesto');
  const netAmount = Math.round((n.totalAmount || 0) - (n.taxAmount || 0));
  const company = resolveCompanyName(n);
  const isCancelled = n.status === 'cancelled' || n.paymentStatus === 'cancelled';
  const isPaid = n.paymentStatus === 'paid';
  const isInReview = n.paymentStatus === 'in_review';
  const isCashReview = isInReview && n.paymentMethod === 'cash';
  const dateHeader = n.chargeMode === 'entregado' ? t('Fecha entrega') : t('Fecha recolección');
  const companyId = String((typeof n.companyId === 'object' ? n.companyId?._id : n.companyId) ?? '');
  const blockReason = remindBlockReason(n, collections.view?.delivery, reminders.cooldown);

  // ── Pagos (parciales) ──
  const payments = n.payments ?? [];
  const amountPaid = paidAmountOf(n);
  const amountDue = amountDueOf(n);
  const isPartiallyPaid = n.paymentStatus === 'partially_paid';
  const progressPct =
    n.totalAmount > 0
      ? Math.min(100, Math.max(0, Math.round((amountPaid / n.totalAmount) * 100)))
      : 0;
  const paidRefs = paidReferencesOf(n);
  // Mismas condiciones de elegibilidad que el endpoint: issued y deuda cobrable.
  const canRegisterPayment =
    !isCancelled &&
    n.status === 'issued' &&
    amountDue > 0 &&
    ['unpaid', 'partially_paid', 'overdue'].includes(n.paymentStatus);
  const creditItems = creditInfo?.items ?? [];
  const creditTotal = creditItems.length
    ? creditItems
        .filter((credit) => !credit.currency || credit.currency === cur)
        .reduce((sum, credit) => sum + (Number(credit.remaining) || 0), 0)
    : Number(creditInfo?.totalRemaining) || 0;

  const openPaymentModal = () => {
    setPaymentAmount(String(amountDue));
    setPaymentDate(todayYMD());
    setPaymentMethod('transfer');
    setPaymentReference('');
    setPaymentComment('');
    setPaymentFormError('');
    setPaymentModal(true);
  };

  const paymentValidationError = () => {
    const amount = parseAmountInput(paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) return t('El monto debe ser mayor que cero.');
    if (!hasValidPrecision(amount, cur)) {
      return cur === 'CLP'
        ? t('El monto debe ser un número entero para {{currency}}.', { currency: cur })
        : t('El monto debe tener hasta 2 decimales.');
    }
    // El contrato acepta el excedente dentro de la tolerancia de redondeo; más allá, 422.
    if (amount > amountDue + roundingTolerance(n.totalAmount)) {
      return t('El monto no puede superar el saldo pendiente ({{amount}}).', {
        amount: formatCurrency(amountDue, cur),
      });
    }
    if (!paymentDate) return t('Selecciona la fecha de pago.');
    if (paymentDate > tomorrowYMD()) return t('La fecha de pago no puede ser posterior a mañana.');
    if (paymentReference.trim().length > 120) {
      return t('La referencia no puede superar los 120 caracteres.');
    }
    if (paymentComment.trim().length > 500) {
      return t('El comentario no puede superar los 500 caracteres.');
    }
    return '';
  };

  const handleRegisterPayment = async () => {
    const validation = paymentValidationError();
    if (validation) {
      setPaymentFormError(validation);
      return;
    }
    const reference = paymentReference.trim();
    const comment = paymentComment.trim();
    setPaymentFormError('');
    try {
      await registerPayment(id, {
        amount: parseAmountInput(paymentAmount),
        paidAt: paidAtIso(paymentDate),
        method: paymentMethod,
        ...(reference ? { reference } : {}),
        ...(comment ? { comment } : {}),
      });
      showToast(t('Pago registrado.'), 'success');
      setPaymentModal(false);
    } catch (err) {
      setPaymentFormError(paymentErrorMessage(err, t, t('No se pudo registrar el pago.'), cur));
    }
  };

  const openVoidPayment = (payment) => {
    setVoidPaymentTarget(payment);
    setVoidPaymentReason('');
    setVoidPaymentError('');
  };

  const handleVoidPayment = async () => {
    const reason = voidPaymentReason.trim();
    if (!reason) {
      setVoidPaymentError(t('Debes indicar un motivo para anular el pago.'));
      return;
    }
    if (reason.length > 500) {
      setVoidPaymentError(t('El motivo no puede superar los 500 caracteres.'));
      return;
    }
    try {
      await voidPayment(id, voidPaymentTarget?._id, reason);
      showToast(t('Pago anulado.'), 'success');
      setVoidPaymentTarget(null);
      setVoidPaymentReason('');
      setVoidPaymentError('');
    } catch (err) {
      setVoidPaymentError(paymentErrorMessage(err, t, t('No se pudo anular el pago.'), cur));
    }
  };

  // Desglose auditable del precio de una línea (tooltip), desde su pricingSnapshot.
  const breakdown = (li) => {
    const s = li.pricingSnapshot;
    if (!s) return undefined;
    const origin =
      s.source === 'zone'
        ? `${t('Tarifa comuna')}${s.municipality ? ` (${s.municipality})` : ''}`
        : s.source === 'fallback'
          ? t('Precio manual')
          : t('Base por defecto');
    let formula = `${Math.round(s.zoneBase ?? 0)}`;
    if (s.multiplier && s.multiplier !== 1) formula += ` × ${s.multiplier}`;
    if (s.additionalCost) formula += ` + ${s.additionalCost}`;
    formula += ` = ${Math.round(s.unit ?? li.totalAmount)}`;
    const parts = [origin, formula];
    if (s.sizeCategory) parts.push(`${t('Tamaño')} ${s.sizeCategory}`);
    return parts.join(' · ');
  };

  // Columnas ordenables. El orden del array es el de la grilla: se usa tanto para la
  // cabecera de escritorio como para el selector de la vista mobile, donde no hay
  // cabeceras que clickear y la funcionalidad quedaría invisible.
  const LINE_SORT_COLUMNS = [
    { key: 'track', label: '#', mobileLabel: t('N° de seguimiento'), align: 'start' },
    { key: 'description', label: t('Descripción'), align: 'start' },
    { key: 'date', label: dateHeader, align: 'start' },
    { key: 'rate', label: t('Tarifa'), align: 'start' },
    { key: 'amount', label: t('Valor'), align: 'end' },
  ];

  const LINE_FILTERS = [
    { value: 'all', label: t('Todos'), count: allItems.length, show: true },
    { value: 'oversized', label: t('Sobredimensionados'), count: oversizedCount, show: oversizedCount > 0 },
    { value: 'zone', label: t('Tarifa por comuna'), count: zoneCount, show: zoneCount > 0 },
    { value: 'manual', label: t('Precio manual'), count: manualCount, show: manualCount > 0 },
    { value: 'returned', label: t('Devueltos'), count: returnedCount, show: returnedCount > 0 },
  ].filter((f) => f.show);

  return (
    <div className="bill-page">
      <button type="button" className="bill-back" onClick={() => navigate('/admin/cobro')}>
        <ArrowLeft className="w-[18px] h-[18px]" />
        {t('Volver a las notas de cobro')}
      </button>

      {/* Cabecera */}
      <div className="bill-card bill-card--pad">
        <div className="bill-doc-head">
          <div>
            <div className="bill-doc-head__title">
              <h2>{t('Nota de cobro')} #{n.folio ?? '—'}</h2>
              <CopyButton value={n.folio} label={t('Copiar folio')} copiedMessage={t('Folio copiado')} />
              <PaymentStatusBadge note={n} withIcon />
              {/* An overdue note that already received money shows both states. */}
              {n.paymentStatus === 'partially_paid' && displayPaymentStatus(n) !== 'partially_paid' && (
                <PaymentStatusBadge status="partially_paid" withIcon />
              )}
            </div>
            <div className="bill-doc-head__meta">
              {company && <><strong>{company}</strong> · </>}
              {holdingNote && (
                <><span className="bill-badge bill-badge--info bill-badge--sm">{t('Holding')}</span> · </>
              )}
              {t(CHARGE_MODE_LABELS[n.chargeMode] ?? n.chargeMode)} ·{' '}
              {t('Periodo')}: <strong>{formatDate(n.periodStart)}</strong> → <strong>{formatDate(n.periodEnd)}</strong>
            </div>
            <NoteDueDateLine note={n} showSource className="mt-1" />
            {/* En lugar del botón "Enviar recordatorio": los recordatorios de holdings son de
                una fase posterior y billing los rechaza. */}
            {holdingNote && !isCancelled && (
              <div className="bill-doc-head__meta mt-1">
                <BellOff className="w-3.5 h-3.5 me-1" style={{ display: 'inline', verticalAlign: '-2px' }} aria-hidden />
                {t('Los recordatorios de cobranza para holdings llegan próximamente: por ahora el seguimiento de sus notas es manual.')}
              </div>
            )}
          </div>

          <div className="bill-doc-head__actions">
            {n.pdfUrl && (
              <Button
                size="sm"
                variant="outline"
                iconLeft={<FileText className="w-4 h-4" />}
                onClick={() => {
                  // La URL viene de la API: se filtra el esquema antes de abrirla
                  // (window.open también ejecuta javascript: con el origen del panel).
                  const href = safeFileUrl(n.pdfUrl);
                  if (href) window.open(href, '_blank', 'noopener');
                }}
              >
                {t('Descargar PDF')}
              </Button>
            )}
            {!isCancelled && canManageCollections && !holdingNote && (
              // El span lleva el motivo: un botón deshabilitado no muestra su title en todos
              // los navegadores.
              <span className="inline-flex" title={blockReason ? t(blockReason) : undefined}>
                <Button
                  size="sm"
                  variant="outline"
                  iconLeft={<BellRing className="w-4 h-4" />}
                  onClick={() => setRemindOpen(true)}
                  disabled={!!blockReason}
                  aria-describedby={blockReason ? 'remind-block-reason' : undefined}
                >
                  {t('Enviar recordatorio')}
                </Button>
                {blockReason && (
                  <span id="remind-block-reason" className="sr-only">
                    {t(blockReason)}
                  </span>
                )}
              </span>
            )}
            {canManageCollections && companyId && (
              <Button
                size="sm"
                variant="ghost"
                iconLeft={<Settings2 className="w-4 h-4" />}
                onClick={() => setRuleOpen(true)}
              >
                {t('Cobranza del comercio')}
              </Button>
            )}
            {!isCancelled && (
              <Button
                size="sm"
                variant="outline"
                iconLeft={<Mail className="w-4 h-4" />}
                onClick={handleNotify}
                loading={notifying}
              >
                {t('Enviar por correo')}
              </Button>
            )}
            {!isCancelled && (
              <Button
                size="sm"
                variant="outline"
                iconLeft={<RefreshCw className="w-4 h-4" />}
                onClick={handleRegeneratePdf}
                loading={regenerating}
              >
                {t('Regenerar PDF')}
              </Button>
            )}
            {/* Con pagos registrados el saldo ya no es cero: billing rechaza la anulación.
                Se oculta en vez de ofrecerla para que no parezca un botón roto. */}
            {!isCancelled && !isPaid && amountPaid <= 0 && (
              <Button
                size="sm"
                variant="danger"
                iconLeft={<Ban className="w-4 h-4" />}
                onClick={() => setVoidModal(true)}
                disabled={voiding}
              >
                {t('Anular nota')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Banner de estado de pago */}
      {isPaid && (
        <div className="bill-connbanner bill-connbanner--connected">
          <span className="bill-connbanner__icon"><CheckCircle2 className="w-5 h-5" /></span>
          <div className="bill-connbanner__body">
            <span className="bill-connbanner__title">{t('Nota pagada')}</span>
            <span className="bill-connbanner__msg">
              {n.paidAt ? t('Pagada el {{date}}', { date: formatDate(n.paidAt) }) : t('Esta nota ya fue cobrada.')}
              {/* Una nota puede cerrarse con varios pagos: se muestran todas sus
                  referencias vigentes, no solo la del último. */}
              {paidRefs.length > 0 && ` · ${t('Ref')} ${paidRefs.join(' · ')}`}
            </span>
          </div>
        </div>
      )}
      {isInReview && (
        <div className="bill-card bill-card--pad mb-3">
          <div className="bill-doc-head">
            <div>
              <div className="bill-doc-head__title">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                  <Hourglass className="w-5 h-5" style={{ color: 'rgb(var(--status-info))' }} />
                  {isCashReview ? t('Pago en efectivo por revisar') : t('Transferencia por revisar')}
                </h3>
              </div>
              <div className="bill-doc-head__meta">
                {isCashReview
                  ? t('El comercio informó un pago en efectivo. Confirma cuando lo recibas o recházalo.')
                  : t('El comercio informó una transferencia. Revisa el comprobante y confirma o rechaza el pago.')}
              </div>
            </div>
            <div className="bill-doc-head__actions">
              {!isCashReview && (
                <Button
                  size="sm"
                  variant="outline"
                  iconLeft={<Eye className="w-4 h-4" />}
                  onClick={handleViewProof}
                  loading={loadingProof}
                >
                  {t('Ver comprobante')}
                </Button>
              )}
              <Button
                size="sm"
                variant="danger"
                iconLeft={<Ban className="w-4 h-4" />}
                onClick={() => setRejectModal(true)}
                disabled={rejecting}
              >
                {t('Rechazar')}
              </Button>
              <Button
                size="sm"
                variant="primary"
                iconLeft={<CheckCircle2 className="w-4 h-4" />}
                onClick={handleConfirm}
                loading={confirming}
              >
                {t('Confirmar pago')}
              </Button>
            </div>
          </div>
          {/* El comercio puede declarar un monto distinto al saldo (abono). El operador
              confirma el monto REAL recibido: viaja como `amount` a integrations. */}
          <div className="bill-field" style={{ maxWidth: '18rem', marginTop: '1rem' }}>
            <label className="bill-label" htmlFor="confirm-received-amount">
              {t('Monto recibido')}
            </label>
            <Input
              id="confirm-received-amount"
              type="number"
              min="1"
              step={cur === 'CLP' ? '1' : '0.01'}
              value={receivedAmount}
              onChange={(e) => setReceivedAmount(e.target.value)}
              invalid={
                receivedAmount !== '' &&
                (!Number.isFinite(parseAmountInput(receivedAmount)) ||
                  parseAmountInput(receivedAmount) <= 0)
              }
            />
            <span className="bill-hint">
              {t('Editable si el monto transferido no coincide con el saldo.')}
            </span>
          </div>
        </div>
      )}
      {isPartiallyPaid && !isCancelled && (
        <div className="bill-connbanner bill-connbanner--idle">
          <span className="bill-connbanner__icon"><CircleDollarSign className="w-5 h-5" /></span>
          <div className="bill-connbanner__body">
            <span className="bill-connbanner__title">{t('Pago parcial registrado')}</span>
            <span className="bill-connbanner__msg">
              {t('Pagado {{paid}} de {{total}}. Saldo pendiente: {{due}}.', {
                paid: formatCurrency(amountPaid, cur),
                total: formatCurrency(n.totalAmount, cur),
                due: formatCurrency(amountDue, cur),
              })}
            </span>
          </div>
        </div>
      )}
      {!isPaid && !isCancelled && !isInReview && !isPartiallyPaid && (
        <PendingBackendNotice className="mb-3">
          {holdingNote
            ? t('La nota de un holding no se paga en línea: cuando recibas el pago, marcala como pagada desde la lista de notas.')
            : t('Aún no hay un pago informado para esta nota. Se actualizará cuando el comercio pague en línea o informe una transferencia.')}
        </PendingBackendNotice>
      )}

      {/* Resumen */}
      <div className="bill-summary">
        <SummaryCard label={t('Líneas')} value={allItems.length} />
        <SummaryCard label={t('Subtotal')} value={formatCurrency(n.subtotal, cur)} />
        <SummaryCard
          label={t('Ajustes')}
          value={`${adjustmentsTotal < 0 ? '−' : adjustmentsTotal > 0 ? '+' : ''}${formatCurrency(Math.abs(adjustmentsTotal), cur)}`}
          negative={adjustmentsTotal < 0}
        />
        <SummaryCard
          label={isInclusive && hasTax ? t('{{label}} incl.', { label: taxLabel }) : taxLabel}
          value={formatCurrency(n.taxAmount, cur)}
        />
        <SummaryCard label={t('Total')} value={formatCurrency(n.totalAmount, cur)} accent />
      </div>

      {/* Pagos: total/pagado/saldo, progreso, historial y registro de abonos. */}
      <div className="bill-card bill-card--pad" style={{ marginTop: '1rem' }}>
        <div className="bill-doc-head">
          <div>
            <h3 className="bill-card__title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Wallet className="w-5 h-5 text-fg-muted" aria-hidden />
              {t('Pagos')}
            </h3>
            <p className="bill-hint" style={{ margin: '0.25rem 0 0' }}>
              {t('Cada pago reduce el saldo. La nota se cierra al cubrir el total, con la tolerancia de redondeo.')}
            </p>
          </div>
          {canRegisterPayment && (
            <Button
              size="sm"
              iconLeft={<Plus className="w-4 h-4" />}
              onClick={openPaymentModal}
            >
              {t('Registrar pago')}
            </Button>
          )}
        </div>

        <div className="bill-summary bill-summary--3" style={{ marginTop: '1rem' }}>
          <SummaryCard label={t('Total')} value={formatCurrency(n.totalAmount, cur)} />
          <SummaryCard label={t('Pagado')} value={formatCurrency(amountPaid, cur)} />
          <SummaryCard
            label={t('Saldo')}
            value={formatCurrency(amountDue, cur)}
            accent={amountDue <= 0}
            warning={amountDue > 0}
          />
        </div>

        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressPct}
          aria-label={t('Progreso de pago')}
          style={{ marginTop: '0.875rem' }}
        >
          <div className="h-2 w-full overflow-hidden rounded-full bg-bg-subtle">
            {/* scaleX en vez de width: anima en el compositor, sin relayout. */}
            <div
              className="h-full w-full rounded-full"
              style={{
                background: 'rgb(var(--status-success))',
                transform: `scaleX(${progressPct / 100})`,
                transformOrigin: 'left',
                transition: 'transform 200ms ease',
              }}
            />
          </div>
          <div className="bill-hint" style={{ marginTop: '0.375rem' }}>
            {t('{{pct}}% pagado', { pct: progressPct })}
          </div>
        </div>

        {creditTotal > 0 && (
          <div className="bill-note" style={{ marginTop: '0.875rem' }}>
            <strong>{t('Saldo a favor del comercio')}:</strong> {formatCurrency(creditTotal, cur)}
          </div>
        )}

        <h4 className="bill-card__title" style={{ marginTop: '1.25rem', marginBottom: '0.75rem' }}>
          {t('Historial de pagos')}
        </h4>
        {payments.length === 0 ? (
          <p className="bill-hint" style={{ margin: 0 }}>
            {t('No hay pagos registrados todavía.')}
          </p>
        ) : isMobile ? (
          <div className="bill-cards">
            {payments.map((payment) => {
              const voidable = isPaymentVoidable(payment);
              const whoKey = registeredByLabelKey(payment);
              return (
                <div
                  key={payment._id}
                  className={`bill-card-item${payment.voidedAt ? ' opacity-60' : ''}`}
                >
                  <div className="bill-card-item__head">
                    <span className={`bill-card-item__title${payment.voidedAt ? ' line-through' : ''}`}>
                      {formatDate(payment.paidAt)}
                    </span>
                    <span className={`bill-td-amount${payment.voidedAt ? ' line-through' : ''}`}>
                      {formatCurrency(payment.amount, payment.currency ?? cur)}
                    </span>
                  </div>
                  <div className="bill-card-item__rows">
                    <div className="bill-card-item__field">
                      <span className="bill-card-item__k">{t('Método')}</span>
                      <span className="bill-card-item__v">{t(paymentMethodLabelKey(payment.method))}</span>
                    </div>
                    <div className="bill-card-item__field">
                      <span className="bill-card-item__k">{t('Referencia')}</span>
                      <span className="bill-card-item__v bill-td-muted">{payment.reference || '—'}</span>
                    </div>
                    <div className="bill-card-item__field">
                      <span className="bill-card-item__k">{t('Registrado por')}</span>
                      <span className="bill-card-item__v bill-td-muted">{whoKey ? t(whoKey, { context: 'pago' }) : '—'}</span>
                    </div>
                    {payment.comment && (
                      <div className="bill-card-item__field bill-card-item__field--full">
                        <span className="bill-card-item__k">{t('Comentario')}</span>
                        <span className="bill-card-item__v bill-td-muted">{payment.comment}</span>
                      </div>
                    )}
                  </div>
                  {payment.voidedAt ? (
                    <div className="bill-note" style={{ marginTop: '0.5rem' }}>
                      <span className="bill-badge bill-badge--neutral bill-badge--sm">{t('Pago anulado')}</span>
                      {payment.voidReason && (
                        <span className="bill-td-sub d-block">
                          {t('Motivo')}: {payment.voidReason}
                        </span>
                      )}
                    </div>
                  ) : voidable ? (
                    <div className="mt-2">
                      <Button size="sm" variant="outline" onClick={() => openVoidPayment(payment)}>
                        {t('Anular pago')}
                      </Button>
                    </div>
                  ) : (
                    <p
                      className="bill-hint"
                      title={t('Este pago se revierte desde el pago del comercio.')}
                      style={{ margin: '0.5rem 0 0', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                    >
                      <Info className="w-3.5 h-3.5" aria-hidden />
                      {t('No se anula desde el panel')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bill-grid" role="table" style={{ '--bill-cols': PAYMENT_COLS }}>
            <div className="bill-grid__head" role="row">
              <span className="bill-grid__hcell" role="columnheader">{t('Fecha de pago')}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Monto')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Método')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Referencia')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Registrado por')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Comentario')}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Acciones')}</span>
            </div>
            {payments.map((payment) => {
              const voidable = isPaymentVoidable(payment);
              const whoKey = registeredByLabelKey(payment);
              return (
                <div
                  key={payment._id}
                  className={`bill-grid__row${payment.voidedAt ? ' opacity-60' : ''}`}
                  role="row"
                >
                  <span className={`bill-td-muted${payment.voidedAt ? ' line-through' : ''}`} role="cell">
                    {formatDate(payment.paidAt)}
                    {payment.voidedAt && (
                      <span className="bill-badge bill-badge--neutral bill-badge--sm ms-2">
                        {t('Pago anulado')}
                      </span>
                    )}
                  </span>
                  <span
                    className={`bill-grid__cell--end bill-td-amount${payment.voidedAt ? ' line-through' : ''}`}
                    role="cell"
                  >
                    {formatCurrency(payment.amount, payment.currency ?? cur)}
                  </span>
                  <span role="cell">{t(paymentMethodLabelKey(payment.method))}</span>
                  <span className="bill-td-muted" role="cell">{payment.reference || '—'}</span>
                  <span className="bill-td-muted" role="cell">{whoKey ? t(whoKey, { context: 'pago' }) : '—'}</span>
                  <span className="bill-td-sub" role="cell">
                    {payment.comment || '—'}
                    {payment.voidedAt && payment.voidReason && (
                      <span className="d-block">{t('Motivo')}: {payment.voidReason}</span>
                    )}
                  </span>
                  <span className="bill-grid__cell--actions" role="cell">
                    {!payment.voidedAt && (
                      voidable ? (
                        <Button size="sm" variant="outline" onClick={() => openVoidPayment(payment)}>
                          {t('Anular pago')}
                        </Button>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-fg-muted"
                          title={t('Este pago se revierte desde el pago del comercio.')}
                        >
                          <Info className="w-3.5 h-3.5" aria-hidden />
                          {t('No se anula desde el panel')}
                        </span>
                      )
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {holdingNote && <HoldingNoteSummary note={n} />}

      {canManageCollections && !holdingNote && (
        <ReminderHistoryCard
          history={reminders.history}
          loading={reminders.loading}
          error={reminders.error}
          onRetry={reminders.reload}
          timeZone={collections.view?.operatorTimeZone}
        />
      )}

      {/* Detalle de líneas */}
      <div className="bill-card bill-card--clip">
        <div className="bill-card__head">
          <h3 className="bill-card__title">
            {t('Detalle de líneas')} <span>({shownItems.length})</span>
          </h3>
        </div>

        {LINE_FILTERS.length > 1 && (
          <div className="d-flex gap-2 flex-wrap" style={{ padding: '0.75rem 1.25rem 0' }}>
            {LINE_FILTERS.map((f) => (
              <Button
                key={f.value}
                size="sm"
                variant={lineFilter === f.value ? 'primary' : 'outline'}
                onClick={() => setLineFilter(f.value)}
              >
                {f.label} ({f.count})
              </Button>
            ))}
          </div>
        )}

        {isMobile ? (
          <>
            {/* En mobile no hay cabeceras que clickear: sin este selector el ordenamiento
                quedaría invisible en teléfono. Misma semántica que la cabecera. */}
            <div className="d-flex gap-2 align-items-center" style={{ padding: '0.75rem 1.25rem 0' }}>
              <Select
                aria-label={t('Ordenar por')}
                value={sort.key ?? ''}
                onChange={(e) =>
                  setSort(e.target.value ? { key: e.target.value, dir: 'desc' } : { key: null, dir: null })
                }
              >
                <option value="">{t('Orden original')}</option>
                {LINE_SORT_COLUMNS.map((c) => (
                  <option key={c.key} value={c.key}>{c.mobileLabel ?? c.label}</option>
                ))}
              </Select>
              {sort.key && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSort((s) => ({ ...s, dir: s.dir === 'desc' ? 'asc' : 'desc' }))}
                  iconLeft={
                    sort.dir === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                  }
                >
                  {sort.dir === 'asc' ? t('Menor a mayor') : t('Mayor a menor')}
                </Button>
              )}
            </div>
            <div className="bill-cards">
            {pagedItems.map((li, i) => (
              <div key={li.packageId ?? `${li.followingNumber}-${i}`} className="bill-card-item">
                <div className="bill-card-item__head">
                  <span className="bill-card-item__title bill-td-track">#{li.followingNumber} <CopyButton value={li.followingNumber} label={t('Copiar número de seguimiento')} copiedMessage={t('Número de seguimiento copiado')} /></span>
                  <span className="bill-td-amount" title={breakdown(li)}>{formatCurrency(li.totalAmount, cur)}</span>
                </div>
                <div className="bill-card-item__rows">
                  <div className="bill-card-item__field bill-card-item__field--full">
                    <span className="bill-card-item__k">{t('Descripción')}</span>
                    <span className="bill-card-item__v bill-td-muted">
                      {li.description}
                      {holdingNote && li.companyName && (
                        <span className="bill-td-sub d-block">{li.companyName}</span>
                      )}
                    </span>
                  </div>
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{dateHeader}</span>
                    <span className="bill-card-item__v bill-td-sub">{formatDate(li.deliveredAt)}</span>
                  </div>
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{t('Tarifa')}</span>
                    <span className="bill-card-item__v">
                      {li.sizeCategory && <span className="bill-pill bill-pill--warning me-1">{li.sizeCategory}</span>}
                      {li.zonePriced && <span className="bill-badge bill-badge--info bill-badge--sm me-1">{t('Comuna')}</span>}
                      {li.manualPriced && <span className="bill-badge bill-badge--neutral bill-badge--sm me-1">{t('Manual')}</span>}
                      {li.returned && <span className="bill-badge bill-badge--reverse bill-badge--sm">{t('Devuelto')}</span>}
                      {!li.sizeCategory && !li.zonePriced && !li.manualPriced && !li.returned && <span className="bill-td-sub">—</span>}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {shownItems.length === 0 && (
              <div className="bill-empty" style={{ padding: '1.5rem' }}>{t('No hay líneas para este filtro.')}</div>
            )}
            </div>
          </>
        ) : (
          <div className="bill-grid" role="table" style={{ '--bill-cols': LINE_COLS }}>
            <div className="bill-grid__head" role="row">
              {LINE_SORT_COLUMNS.map((c) => (
                <SortHeader
                  key={c.key}
                  colKey={c.key}
                  label={c.label}
                  align={c.align}
                  sort={sort}
                  onSort={toggleSort}
                  title={t('Ordenar por {{col}}', { col: c.label })}
                />
              ))}
            </div>
            {pagedItems.map((li, i) => (
              // key por paquete y no por índice: al reordenar, una key posicional hace que
              // React reutilice los nodos por posición en vez de seguir a la línea.
              <div key={li.packageId ?? `${li.followingNumber}-${i}`} className="bill-grid__row" role="row">
                <span className="bill-td-track" role="cell">{li.followingNumber} <CopyButton value={li.followingNumber} label={t('Copiar número de seguimiento')} copiedMessage={t('Número de seguimiento copiado')} /></span>
                <span className="bill-td-muted" role="cell">
                  {li.description}
                  {/* En la nota del holding cada línea es de una de sus empresas. */}
                  {holdingNote && li.companyName && (
                    <span className="bill-td-sub d-block">{li.companyName}</span>
                  )}
                </span>
                <span className="bill-td-sub" role="cell">{formatDate(li.deliveredAt)}</span>
                <span role="cell">
                  {li.sizeCategory && <span className="bill-pill bill-pill--warning me-1">{li.sizeCategory}</span>}
                  {li.zonePriced && <span className="bill-badge bill-badge--info bill-badge--sm me-1">{t('Comuna')}</span>}
                  {li.manualPriced && <span className="bill-badge bill-badge--neutral bill-badge--sm me-1">{t('Manual')}</span>}
                  {li.returned && <span className="bill-badge bill-badge--reverse bill-badge--sm">{t('Devuelto')}</span>}
                  {!li.sizeCategory && !li.zonePriced && !li.manualPriced && !li.returned && <span className="bill-td-sub">—</span>}
                </span>
                <span className="bill-grid__cell--end bill-td-amount" role="cell" title={breakdown(li)}>
                  {formatCurrency(li.totalAmount, cur)}
                </span>
              </div>
            ))}
            {shownItems.length === 0 && (
              <div className="bill-empty" style={{ padding: '1.5rem' }}>{t('No hay líneas para este filtro.')}</div>
            )}
          </div>
        )}

        {visibleLines < shownItems.length && (
          <div className="bill-pagination">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setVisibleLines((v) => v + LINE_PAGE)}
            >
              {t('Ver más')} ({shownItems.length - visibleLines})
            </Button>
          </div>
        )}

        {/* Totales */}
        <div className="bill-pagination" style={{ justifyContent: 'flex-end', gap: '2rem', flexWrap: 'wrap' }}>
          <span><span className="bill-td-sub me-2">{t('Subtotal')}</span><span className="bill-td-amount">{formatCurrency(n.subtotal, cur)}</span></span>
          {(n.adjustments ?? []).map((a, i) => (
            <span key={i}>
              <span className="bill-td-sub me-2">{a.type === 'discount' ? t('Descuento') : t('Recargo')}: {a.label}</span>
              <span className={a.type === 'discount' ? 'bill-td-neg' : 'bill-td-amount'}>
                {a.type === 'discount' ? '−' : '+'}{formatCurrency(a.amount, cur)}
              </span>
            </span>
          ))}
          {/* exclusive: el IVA se muestra antes del Total (base neta). */}
          {hasTax && !isInclusive && (
            <span><span className="bill-td-sub me-2">{taxLabel}</span><span className="bill-td-amount">{formatCurrency(n.taxAmount, cur)}</span></span>
          )}
          <span><span className="bill-td-strong me-2">{t('Total')}</span><span className="bill-td-amount" style={{ fontSize: '1rem' }}>{formatCurrency(n.totalAmount, cur)}</span></span>
          {/* inclusive: el Total no cambia; se desglosa el neto y el IVA incluido. */}
          {hasTax && isInclusive && (
            <>
              <span><span className="bill-td-sub me-2">{t('Neto')}</span><span className="bill-td-amount">{formatCurrency(netAmount, cur)}</span></span>
              <span><span className="bill-td-sub me-2">{t('{{label}} incluido', { label: taxLabel })}</span><span className="bill-td-amount">{formatCurrency(n.taxAmount, cur)}</span></span>
            </>
          )}
        </div>
        {hasTax && isInclusive && (
          <p className="bill-hint" style={{ textAlign: 'right', padding: '0 1.25rem 0.75rem' }}>
            {t('Los valores de las tarifas incluyen IVA.')}
          </p>
        )}
      </div>

      {canManageCollections && !holdingNote && (
        <SendReminderModal
          open={remindOpen}
          onClose={() => setRemindOpen(false)}
          note={n}
          cooldown={reminders.cooldown}
          onQueued={reminders.refreshAfterQueue}
        />
      )}
      {canManageCollections && companyId && (
        <CommerceCollectionRuleDrawer
          open={ruleOpen}
          onClose={() => setRuleOpen(false)}
          companyId={companyId}
          companyName={company || null}
        />
      )}

      {/* Modal anular */}
      <Modal
        open={voidModal}
        onClose={() => setVoidModal(false)}
        size="sm"
        title={t('Anular nota de cobro')}
        footer={
          <>
            <Button variant="outline" onClick={() => setVoidModal(false)}>{t('Cancelar')}</Button>
            <Button variant="danger" onClick={handleVoid} loading={voiding}>{t('Confirmar anulación')}</Button>
          </>
        }
      >
        <p className="bill-hint" style={{ marginTop: 0 }}>
          {t('Los paquetes incluidos volverán a quedar disponibles para una nueva nota. Esta acción no se puede deshacer.')}
        </p>
        <div className="bill-field">
          <label className="bill-label">{t('Motivo de la anulación')} <span className="text-status-error">*</span></label>
          <textarea
            className="bill-textarea"
            rows={3}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder={t('Ej: error en el periodo, comercio equivocado…')}
            autoFocus
          />
        </div>
      </Modal>

      {/* Modal registrar pago (parcial o total) */}
      <Modal
        open={paymentModal}
        onClose={() => setPaymentModal(false)}
        size="sm"
        title={t('Registrar pago')}
        footer={
          <>
            <Button variant="outline" onClick={() => setPaymentModal(false)} disabled={registeringPayment}>
              {t('Cancelar')}
            </Button>
            <Button onClick={handleRegisterPayment} loading={registeringPayment}>
              {t('Registrar pago')}
            </Button>
          </>
        }
      >
        <p className="bill-hint" style={{ marginTop: 0 }}>
          {t('Saldo pendiente')}: <strong>{formatCurrency(amountDue, cur)}</strong>.{' '}
          {t('El pago puede ser parcial: la nota seguirá abierta por el saldo.')}
        </p>

        {paymentFormError && (
          <p className="bill-hint" role="alert" style={{ color: 'rgb(var(--status-error))' }}>
            {paymentFormError}
          </p>
        )}

        <div className="bill-field">
          <label className="bill-label" htmlFor="payment-amount">
            {t('Monto del pago')} <span className="text-status-error">*</span>
          </label>
          <Input
            id="payment-amount"
            type="number"
            min="1"
            step={cur === 'CLP' ? '1' : '0.01'}
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            autoFocus
          />
        </div>

        <div className="bill-field">
          <label className="bill-label" htmlFor="payment-date">
            {t('Fecha de pago')} <span className="text-status-error">*</span>
          </label>
          <Input
            id="payment-date"
            type="date"
            max={tomorrowYMD()}
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
        </div>

        <div className="bill-field">
          <label className="bill-label" htmlFor="payment-method">{t('Método de pago')}</label>
          <Select
            id="payment-method"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
          >
            {PAYMENT_METHODS.map((method) => (
              <option key={method} value={method}>
                {t(paymentMethodLabelKey(method))}
              </option>
            ))}
          </Select>
        </div>

        <div className="bill-field">
          <label className="bill-label" htmlFor="payment-reference">{t('Referencia (opcional)')}</label>
          <Input
            id="payment-reference"
            value={paymentReference}
            maxLength={120}
            onChange={(e) => setPaymentReference(e.target.value)}
          />
        </div>

        <div className="bill-field">
          <label className="bill-label" htmlFor="payment-comment">{t('Comentario (opcional)')}</label>
          <textarea
            id="payment-comment"
            className="bill-textarea"
            rows={3}
            maxLength={500}
            value={paymentComment}
            onChange={(e) => setPaymentComment(e.target.value)}
            placeholder={t('Ej: transferencia parcial por tope bancario…')}
          />
        </div>
      </Modal>

      {/* Modal anular pago */}
      <Modal
        open={!!voidPaymentTarget}
        onClose={() => setVoidPaymentTarget(null)}
        size="sm"
        title={t('Anular pago')}
        footer={
          <>
            <Button variant="outline" onClick={() => setVoidPaymentTarget(null)} disabled={voidingPayment}>
              {t('Cancelar')}
            </Button>
            <Button variant="danger" onClick={handleVoidPayment} loading={voidingPayment}>
              {t('Anular pago')}
            </Button>
          </>
        }
      >
        {voidPaymentTarget && (
          <p className="bill-hint" style={{ marginTop: 0 }}>
            {formatCurrency(voidPaymentTarget.amount, voidPaymentTarget.currency ?? cur)} ·{' '}
            {formatDate(voidPaymentTarget.paidAt)} · {t(paymentMethodLabelKey(voidPaymentTarget.method))}
          </p>
        )}
        {voidPaymentError && (
          <p className="bill-hint" role="alert" style={{ color: 'rgb(var(--status-error))' }}>
            {voidPaymentError}
          </p>
        )}
        <div className="bill-field">
          <label className="bill-label" htmlFor="void-payment-reason">
            {t('Motivo de la anulación del pago')} <span className="text-status-error">*</span>
          </label>
          <textarea
            id="void-payment-reason"
            className="bill-textarea"
            rows={3}
            value={voidPaymentReason}
            onChange={(e) => setVoidPaymentReason(e.target.value)}
            placeholder={t('Ej: se registró dos veces el mismo pago…')}
            autoFocus
          />
        </div>
      </Modal>

      {/* Modal rechazar pago */}
      <Modal
        open={rejectModal}
        onClose={() => setRejectModal(false)}
        size="sm"
        title={t('Rechazar pago por transferencia')}
        footer={
          <>
            <Button variant="outline" onClick={() => setRejectModal(false)}>{t('Cancelar')}</Button>
            <Button variant="danger" onClick={handleReject} loading={rejecting}>{t('Rechazar pago')}</Button>
          </>
        }
      >
        <p className="bill-hint" style={{ marginTop: 0 }}>
          {t('La nota volverá a quedar impaga y el comercio podrá informar el pago nuevamente.')}
        </p>
        <div className="bill-field">
          <label className="bill-label">{t('Motivo (opcional)')}</label>
          <textarea
            className="bill-textarea"
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('Ej: el comprobante no coincide con el monto…')}
            autoFocus
          />
        </div>
      </Modal>
    </div>
  );
};

export default CobroNoteDetail;
